import { useLayoutEffect, useRef, useState } from 'react';
import { modals } from '@mantine/modals';

import Tab from '../PractitionersList/ListCustomersTab';
import { Tab as BaseballCardTab } from '../PractitionerBaseballCard';
import BaseballCard from '../PractitionerBaseballCard';
import PractitionersList from '../PractitionersList';
import { openLeadDrawer } from 'components/LeadDrawer';
import { openPractitionerListDrawer } from 'components/PractitionerListDrawer';
import { store } from 'stores/Store';

interface Props {
  defaultTab?: Tab;
  onFindOnMap?: (practitioner: any, latLon: [number, number]) => void;
}

// Find the element that actually scrolls the practitioner list.
// Since EIS-1070 the list owns its scroll: the rows region INSIDE the component
// ([data-testid="practitioner-list-body"], a DESCENDANT of our root) is the scroller,
// so we look inside first. The ancestor walk is kept as a fallback for any host where
// the panel still scrolls (pre-EIS-1070 semantics: mantine Drawer content / side section).
const findScrollContainer = (el: HTMLElement | null): HTMLElement | null => {
  if (!el) return null;
  const inner = el.querySelector<HTMLElement>('[data-testid="practitioner-list-body"]');
  if (inner) return inner;
  let node = el.parentElement ?? null;
  while (node) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (/(auto|scroll)/.test(overflowY) && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return null;
};

const PractitionersListWithDetails = ({ defaultTab = Tab.ALL, onFindOnMap }: Props) => {
  const [selectedBaseballCardTab, setSelectedBaseballCardTab] = useState<BaseballCardTab>();
  const [onViewPractitioner, setOnViewPractitioner] = useState<any>(
    store.currentPractitioner && Object.keys(store.currentPractitioner).length > 0 ?
      store.currentPractitioner : null
  );

  // EIS-1065: previously this component swapped the list OUT for the baseball card
  // (`if (onViewPractitioner) return <BaseballCard/>`), unmounting the list so that on
  // "back" it remounted at the top (#1) and lost its loaded pages. Now the list stays
  // mounted (hidden behind the card) and we remember/restore the list's scroll position
  // (since EIS-1070 the scroller is the rows region INSIDE the list, not the host panel),
  // so back returns the user exactly where they were (e.g. #147). The card is
  // still rendered in normal flow — identical stacking to before — so downstream actions
  // (add-to-route, check-ins) and their modals are unaffected.
  const rootRef = useRef<HTMLDivElement>(null);
  const savedScrollTopRef = useRef(0);

  // After the card closes and the list is re-shown, restore the saved scroll position.
  // useLayoutEffect runs after the DOM updates but before paint, so there is no flicker.
  useLayoutEffect(() => {
    if (!onViewPractitioner) {
      const scrollContainer = findScrollContainer(rootRef.current);
      if (scrollContainer) scrollContainer.scrollTop = savedScrollTopRef.current;
    }
  }, [onViewPractitioner]);

  // EIS-1070: the list fills its container and scrolls internally, so every host in this
  // chain must hand it a height:100% box (the hosts of THIS component already size their
  // panel: drawer body / ChatbotV1 view div).
  return (
    <div ref={rootRef} style={{ height: '100%' }}>
      {/* List stays mounted (just hidden) so its loaded pages survive while the card is open. */}
      <div style={{ display: onViewPractitioner ? 'none' : undefined, height: '100%' }}>
        <PractitionersList
          defaultTab={defaultTab}
          onPractitionerSelect={(initialTab) => {
            // Capture where the user is BEFORE the card hides the list.
            savedScrollTopRef.current = findScrollContainer(rootRef.current)?.scrollTop ?? 0;
            setSelectedBaseballCardTab(initialTab);
            setOnViewPractitioner(store.currentPractitioner);
          }}
          onOpenLeadCreate={() => {
            modals.closeAll();
            openLeadDrawer(null, {
              hideHeader: true,
            }, () => {
              openPractitionerListDrawer({}, Tab.LEAD);
            });
          }}
        />
      </div>

      {onViewPractitioner && (
        <div data-testid="practitioner-baseball-card">
          <BaseballCard
            backHandler={() => {
              store.setCurrentPractitioner(null);
              setOnViewPractitioner(null);
            }}
            defaultTab={selectedBaseballCardTab ?? BaseballCardTab.SUMMARY}
            generalNotes=""
            onFindOnMap={onFindOnMap}
          />
        </div>
      )}
    </div>
  );
};

export default PractitionersListWithDetails;
